import { useCallback, useMemo } from "react";

import useAuthStore from "@/store/auth";

import axios from "./axios";
import { usePrivateAxios } from "./use-private-axios";

export interface AddToBagPayload {
	productId: string;
	vendorId: string;
	quantity: number;
	selectedSize?: string;
	selectedColor?: string;
	isWishlist?: boolean;
	notes?: string;
}

export interface UpdateBagItemPayload {
	quantity?: number;
	selectedSize?: string;
	selectedColor?: string;
	notes?: string;
}

export interface UpdateBagPayload {
	isWishlist?: boolean;
	notes?: string;
}

function authHeaders(): Record<string, string> {
	const token = useAuthStore.getState().tokens?.accessToken;
	return token
		? { Authorization: `Bearer ${token}`, authorization: `Bearer ${token}` }
		: {};
}

// Plain functions (keep for backward compatibility)
export async function getCartBags() {
	const res = await axios.get("/bag/cart", { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function getWishlistBags() {
	const res = await axios.get("/bag/wishlist", { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function getAllBags() {
	const res = await axios.get("/bag/all", { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function getBagSummary() {
	const res = await axios.get("/bag/summary", { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function getBagById(bagId: string) {
	const res = await axios.get(`/bag/${bagId}`, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function getOrderHistory() {
	const res = await axios.get("/bag/history", { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function addToBag(payload: AddToBagPayload) {
	const res = await axios.post("/bag/add", payload, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function updateBagItem(
	bagId: string,
	productId: string,
	payload: UpdateBagItemPayload,
	currentSelectedSize?: string,
	currentSelectedColor?: string
) {
	const params = new URLSearchParams();
	if (currentSelectedSize)
		params.append("currentSelectedSize", currentSelectedSize);
	if (currentSelectedColor)
		params.append("currentSelectedColor", currentSelectedColor);

	const res = await axios.put(
		`/bag/item/${bagId}/${productId}${params.toString() ? `?${params.toString()}` : ""}`,
		payload,
		{ headers: authHeaders() }
	);
	return res.data?.data ?? res.data;
}

export async function removeFromBag(
	bagId: string,
	productId: string,
	selectedSize?: string,
	selectedColor?: string,
	quantity?: number
) {
	const params = new URLSearchParams();
	if (selectedSize)
		params.append("selectedSize", selectedSize);
	if (selectedColor)
		params.append("selectedColor", selectedColor);
	if (quantity !== undefined)
		params.append("quantity", quantity.toString());

	const res = await axios.delete(
		`/bag/item/${bagId}/${productId}${params.toString() ? `?${params.toString()}` : ""}`,
		{ headers: authHeaders() }
	);
	return res.data?.data ?? res.data;
}

export async function moveBagToWishlist(bagId: string) {
	const res = await axios.put(`/bag/move-to-wishlist/${bagId}`, undefined, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function moveBagToCart(bagId: string) {
	const res = await axios.put(`/bag/move-to-cart/${bagId}`, undefined, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function updateBag(bagId: string, payload: UpdateBagPayload) {
	const res = await axios.put(`/bag/${bagId}`, payload, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function clearBag(bagId: string) {
	const res = await axios.delete(`/bag/${bagId}`, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

export async function negotiateWithVendor(bagId: string) {
	const res = await axios.post(`/bag/negotiate/${bagId}`, undefined, { headers: authHeaders() });
	return res.data?.data ?? res.data;
}

// Legacy alias for backward compatibility
export async function getBag() {
	return getCartBags();
}

// React hook version that uses axios with auto-refreshing tokens
export function useBagApi() {
	const privateAxios = usePrivateAxios();

	const getCartBagsCb = useCallback(async () => {
		const res = await privateAxios.get("/bag/cart");
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const getWishlistBagsCb = useCallback(async () => {
		const res = await privateAxios.get("/bag/wishlist");
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const getAllBagsCb = useCallback(async () => {
		const res = await privateAxios.get("/bag/all");
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const getBagSummaryCb = useCallback(async () => {
		const res = await privateAxios.get("/bag/summary");
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const getBagByIdCb = useCallback(async (bagId: string) => {
		const res = await privateAxios.get(`/bag/${bagId}`);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const getOrderHistoryCb = useCallback(async () => {
		const res = await privateAxios.get("/bag/history");
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const addToBagCb = useCallback(async (payload: AddToBagPayload) => {
		const res = await privateAxios.post("/bag/add", payload);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const updateBagItemCb = useCallback(async (
		bagId: string,
		productId: string,
		payload: UpdateBagItemPayload,
		currentSelectedSize?: string,
		currentSelectedColor?: string
	) => {
		const params = new URLSearchParams();
		if (currentSelectedSize)
			params.append("currentSelectedSize", currentSelectedSize);
		if (currentSelectedColor)
			params.append("currentSelectedColor", currentSelectedColor);
		const res = await privateAxios.put(
			`/bag/item/${bagId}/${productId}${params.toString() ? `?${params.toString()}` : ""}`,
			payload
		);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const removeFromBagCb = useCallback(async (
		bagId: string,
		productId: string,
		selectedSize?: string,
		selectedColor?: string,
		quantity?: number
	) => {
		const params = new URLSearchParams();
		if (selectedSize)
			params.append("selectedSize", selectedSize);
		if (selectedColor)
			params.append("selectedColor", selectedColor);
		if (quantity !== undefined)
			params.append("quantity", String(quantity));
		const res = await privateAxios.delete(
			`/bag/item/${bagId}/${productId}${params.toString() ? `?${params.toString()}` : ""}`
		);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const moveBagToWishlistCb = useCallback(async (bagId: string) => {
		const res = await privateAxios.put(`/bag/move-to-wishlist/${bagId}`);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const moveBagToCartCb = useCallback(async (bagId: string) => {
		const res = await privateAxios.put(`/bag/move-to-cart/${bagId}`);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const updateBagCb = useCallback(async (bagId: string, payload: UpdateBagPayload) => {
		const res = await privateAxios.put(`/bag/${bagId}`, payload);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const clearBagCb = useCallback(async (bagId: string) => {
		const res = await privateAxios.delete(`/bag/${bagId}`);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	const negotiateWithVendorCb = useCallback(async (bagId: string) => {
		const res = await privateAxios.post(`/bag/negotiate/${bagId}`);
		return res.data?.data ?? res.data;
	}, [privateAxios]);

	return useMemo(() => ({
		getCartBags: getCartBagsCb,
		getWishlistBags: getWishlistBagsCb,
		getAllBags: getAllBagsCb,
		getBagSummary: getBagSummaryCb,
		getBagById: getBagByIdCb,
		getOrderHistory: getOrderHistoryCb,
		addToBag: addToBagCb,
		updateBagItem: updateBagItemCb,
		removeFromBag: removeFromBagCb,
		moveBagToWishlist: moveBagToWishlistCb,
		moveBagToCart: moveBagToCartCb,
		updateBag: updateBagCb,
		clearBag: clearBagCb,
		negotiateWithVendor: negotiateWithVendorCb
	}), [
		getCartBagsCb,
		getWishlistBagsCb,
		getAllBagsCb,
		getBagSummaryCb,
		getBagByIdCb,
		getOrderHistoryCb,
		addToBagCb,
		updateBagItemCb,
		removeFromBagCb,
		moveBagToWishlistCb,
		moveBagToCartCb,
		updateBagCb,
		clearBagCb,
		negotiateWithVendorCb
	]);
}
